import { Link } from "react-router-dom";
import { LegalShell, Section, UPDATED } from "@/pages/Legal";

const A = ({ to, children }) => <Link to={to} className="text-[#B8A4FF] hover:underline">{children}</Link>;

export default function Privacy() {
  return (
    <LegalShell eyebrow="LEGAL" title="Privacy policy" updated={UPDATED} testid="privacy-page"
      seo={{ title: "Privacy Policy", description: "How Studivault (operated by Bloc Metric, India) collects, uses, shares and protects your personal data under the DPDP Act 2023, IT Act 2000 and GDPR/CCPA principles.", path: "/privacy-policy" }}>
      <p>
        This Privacy Policy explains how <strong>Bloc Metric</strong> ("we", "us", "our"), the operator of
        Studivault at <strong>studivault.com</strong> (the "Service"), collects, uses, shares, stores and
        protects your personal data. It is published in accordance with the Digital Personal Data
        Protection Act, 2023 ("DPDP Act"), the Information Technology Act, 2000 and the Information
        Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or
        Information) Rules, 2011, and the Information Technology (Intermediary Guidelines and Digital
        Media Ethics Code) Rules, 2021. By using the Service you acknowledge this Policy.
      </p>

      <Section n={1} title="Data fiduciary">
        <p>
          For the purposes of the DPDP Act, Bloc Metric, India, is the <strong>Data Fiduciary</strong> that
          determines the purpose and means of processing your personal data. Contact:{" "}
          <a href="mailto:shaurya@sanghviinfo.com" className="text-[#B8A4FF] hover:underline">shaurya@sanghviinfo.com</a>.
          Grievances may be raised with our Grievance Officer (see <A to="/grievance">Grievance redressal</A>).
        </p>
      </Section>

      <Section n={2} title="Personal data we collect">
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Account data</strong> — name, email address, a one-way hashed password, and your age/guardian and Terms acknowledgement. If you use Google sign-in, we receive your name, email and Google account identifier from Google.</li>
          <li><strong>Security data</strong> — optional two-factor authentication secrets (stored encrypted), login attempt records, and email-verification / password-reset tokens.</li>
          <li><strong>Study content</strong> — text, PDFs, documents, images, audio, video, YouTube links and voice recordings you upload or record, and the notes, flashcards, quizzes, exams and tutor responses generated from them.</li>
          <li><strong>Activity data</strong> — XP, streaks, flashcard ratings, quiz and exam attempts, gems balance and redemptions, study-room participation, published packs, leaderboard position.</li>
          <li><strong>Payment data</strong> — plan purchased, amount, currency, Razorpay order/payment identifiers and status. Card, UPI and bank details are collected directly by Razorpay; we never receive or store them.</li>
          <li><strong>Communications</strong> — messages you send via the contact form or email.</li>
          <li><strong>Technical data</strong> — IP address, browser/device information, and error logs needed to secure and operate the Service.</li>
        </ul>
        <p>We do not intentionally collect sensitive personal data such as health, biometric, financial account or government ID data. Please do not upload such data in study material.</p>
      </Section>

      <Section n={3} title="Purposes and legal basis">
        <p>We process personal data only for lawful purposes for which you have given consent, or for legitimate uses permitted under Section 7 of the DPDP Act, namely to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>create and secure your account, authenticate you and prevent fraud or abuse;</li>
          <li>generate study material, flashcards, quizzes, exams and tutor answers using AI processing of the content you submit;</li>
          <li>run gamification, leaderboards, study rooms and the pack marketplace;</li>
          <li>process payments and activate your plan, and keep records required by tax and accounting law;</li>
          <li>send transactional emails (verification, password reset, receipts, service notices) and respond to support requests;</li>
          <li>show advertising to free-plan users, only if you accept non-essential cookies;</li>
          <li>comply with legal obligations and lawful requests from authorities.</li>
        </ul>
        <p>For users in the EEA/UK, our legal bases are performance of contract, consent (e.g., advertising cookies), legitimate interests (security, service improvement) and legal obligation.</p>
      </Section>

      <Section n={4} title="Consent and withdrawal">
        <p>
          You give consent when you create an account and when you accept non-essential cookies. You may
          withdraw consent at any time, as easily as you gave it, by changing your cookie choice
          (<A to="/cookie-policy">Cookie policy</A>), deleting content, deleting your account, or emailing us.
          Withdrawal does not affect processing carried out before withdrawal, and some features may stop
          working without the related data.
        </p>
      </Section>

      <Section n={5} title="Third-party processors">
        <p>We share personal data only with service providers that process it on our behalf under contractual obligations:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>DeepSeek</strong> — AI language model that generates study material from the content you submit.</li>
          <li><strong>Google (Gemini)</strong> — AI processing for image, audio and video understanding and voice transcription.</li>
          <li><strong>Google (Sign-in)</strong> — optional authentication with your Google account.</li>
          <li><strong>Google AdSense</strong> — advertising shown to free-plan users, loaded only after you accept non-essential cookies.</li>
          <li><strong>Google Analytics</strong> — usage analytics, loaded only after you accept non-essential cookies.</li>
          <li><strong>Meta Platforms (Meta Pixel)</strong> — measures our Facebook/Instagram ads (page views, sign-ups, studies created), loaded only after you accept non-essential cookies.</li>
          <li><strong>Razorpay</strong> — payment processing for plan purchases.</li>
          <li><strong>Cloudflare R2</strong> — temporary storage of uploaded media files.</li>
          <li><strong>MongoDB (cloud database hosting)</strong> — storage of account and study data.</li>
          <li><strong>Email (SMTP) provider</strong> — delivery of transactional emails.</li>
          <li><strong>YouTube</strong> — retrieval of public video transcripts when you submit a YouTube link.</li>
        </ul>
        <p>We do not sell your personal data. We may disclose data where required by law, court order or a government agency, or to protect the rights, safety and property of users or the public.</p>
      </Section>

      <Section n={6} title="International transfers">
        Some processors store or process data outside India (for example in the United States, the EU or
        other countries). Such transfers are made in compliance with the DPDP Act and any restrictions
        notified by the Government of India, and, for EEA/UK users, with appropriate safeguards.
      </Section>

      <Section n={7} title="Retention">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Account and study data are kept while your account is active and deleted when you delete them or your account, or when the purpose is no longer served.</li>
          <li>Uploaded audio/video source files are automatically deleted from storage within about one hour; transcripts remain with the study.</li>
          <li>Payment and invoice records are retained for up to 8 years as required by Indian tax and accounting laws.</li>
          <li>Security and error logs are kept for a limited period for security and debugging, and as required by law (including CERT-In directions).</li>
        </ul>
      </Section>

      <Section n={8} title="Your rights as a data principal">
        <p>Under the DPDP Act you have the right to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>obtain a summary of the personal data we process and the processing activities;</li>
          <li>request correction, completion, updating or erasure of your personal data;</li>
          <li>withdraw consent;</li>
          <li>grievance redressal through our Grievance Officer, and thereafter complain to the Data Protection Board of India;</li>
          <li>nominate another person to exercise your rights in the event of your death or incapacity.</li>
        </ul>
        <p>
          Users in the EEA/UK additionally have rights of access, rectification, erasure, restriction,
          objection, portability and to lodge a complaint with a supervisory authority. California residents
          have the right to know, delete and correct personal information and to non-discrimination; we do
          not sell or "share" personal information for cross-context behavioural advertising except via
          AdSense cookies you may reject. To exercise any right, email{" "}
          <a href="mailto:shaurya@sanghviinfo.com" className="text-[#B8A4FF] hover:underline">shaurya@sanghviinfo.com</a>.
          We may verify your identity before acting and will respond within the time required by law.
        </p>
      </Section>

      <Section n={9} title="Children">
        The Service is intended for users aged 18 and above. Users under 18 may use the Service only with
        the verifiable consent and supervision of a parent or lawful guardian, who accepts these terms on
        their behalf. We do not knowingly track, behaviourally monitor or target advertising at children.
        If you believe a child is using the Service without guardian consent, contact us and we will
        delete the account.
      </Section>

      <Section n={10} title="Security">
        We implement reasonable security practices, including encryption in transit (HTTPS), bcrypt
        password hashing, encrypted two-factor secrets, access controls, rate limiting and login lockout,
        and restricted administrative access. No system is perfectly secure, and we cannot guarantee
        absolute security.
      </Section>

      <Section n={11} title="Personal data breach">
        In the event of a personal data breach, we will notify affected users and the Data Protection
        Board of India, and report incidents to CERT-In, in the form and within the timelines required by
        law, describing the nature of the breach, its likely consequences and the measures taken.
      </Section>

      <Section n={12} title="Cookies">
        See our <A to="/cookie-policy">Cookie policy</A> for details on essential storage and optional advertising cookies.
      </Section>

      <Section n={13} title="Changes">
        We may update this Policy from time to time. The "Last updated" date shows the latest version;
        material changes will be notified in the app or by email. Continued use after changes take effect
        means you accept the updated Policy.
      </Section>

      <Section n={14} title="Contact">
        Bloc Metric, India · <a href="mailto:shaurya@sanghviinfo.com" className="text-[#B8A4FF] hover:underline">shaurya@sanghviinfo.com</a> ·{" "}
        <A to="/contact">Contact form</A>
      </Section>
    </LegalShell>
  );
}
